@if (inEditor()) {
  <!-- SBUX-1036: on the editor's page this form is the workbook's item header, as the prototype's editor has it. Row 1 is
       the editor's title bar (rename, save state, Download and share, the item menu, Close), with this form's own
       Archive and Trash in it; row 2 the dates, the sheet count and the knowledge panel's toggle; then the tabs. -->
  <app-sheets-expanded-header [sheet]="headerSheet()" (closed)="handleClose()" (resize)="handleResize($event)">
    <div headerLifecycle class="editor-lifecycle" [appPreventDirtyClick]="dirtyFormRef">
      <stch-quick-action
        appearance="sapphire-toggle"
        size="m"
        icon="sapphire-archive2"
        [active]="form.controls.archived.value"
        [matTooltip]="form.controls.archived.value ? Labels.RestoreFromArchive : Labels.Archive"
        [disabled]="!sheet?.id"
        (onClick)="archive()"
      />
      <stch-trash-action [disabled]="!sheet?.id" [deleted]="form.controls.deleted.value" (onClick)="delete()" />
    </div>
  </app-sheets-expanded-header>

  <!-- The prototype's meta row: "Created: … | Last updated: … | <n> sheets", where the workbook lives, and on the
       right its "Show stitched items", our knowledge panel's toggle. -->
  <div class="meta-row" data-testid="sheet-editor-meta">
    <app-stitch-timestamps
      class="meta-row-dates"
      [createdAt]="sheet?.createdAt"
      [updatedAt]="sheet?.updatedAt"
      [updatedLabel]="S['Last updated']"
      [updatedBy]="sheet?.lastActor === 'stitchy' ? 'Stitchy' : null"
    />
    @if (sheetCountLabel(); as count) {
      <span class="meta-row-separator"></span>
      <span class="meta-row-count">{{ count }}</span>
    }
    <span class="filler"></span>
    @if (headerFolder()) {
      <ng-container [ngTemplateOutlet]="folderBadge" />
    } @else if (locationBadge(); as badge) {
      <app-search-location-badge class="min-w-0" [type]="badge.type" [name]="badge.name ?? ''" />
    }
    <stch-knowledge-button [active]="editorKnowledgeOpen()" (click)="editorKnowledgeOpen.set(!editorKnowledgeOpen())" />
  </div>
} @else {
<div class="heading skin-detail-heading" (resize)="handleResize($event)">
  @if (canGoBack()) {
    <app-button2 appearance="secondary-ghost" size="s" [ariaLabel]="S['Go back']" (click)="handleGoBack()">
      <app-icon name="chevron-left" />
    </app-button2>
  }

  <app-input
    class="input"
    appearance="sapphire-inline"
    [inputFormControl]="form.controls.title"
    placeholder="New Workbook"
    [errorPlaceholder]="form.controls.title.hasError('required') ? 'Add title' : undefined"
    [validationMessages]="titleMessages"
    [withErrors]="submitted"
    size="s"
  />

  <div class="icons">
    <div [appPreventDirtyClick]="dirtyFormRef" class="contents">
      @if (!contracted) {
        <!-- [MUST-002] 'Bug' debug quick-action hidden per task; uncomment to re-enable
        <stch-quick-action
          appearance="sapphire-toggle"
          size="m"
          icon="bug"
          [active]="selectedTab === 'debug'"
          matTooltip="Debug"
          (onClick)="selectedTab = 'debug'"
        />
        -->

        <stch-quick-action
          appearance="sapphire-toggle"
          size="m"
          icon="sapphire-archive2"
          [active]="form.controls.archived.value"
          [matTooltip]="form.controls.archived.value ? Labels.RestoreFromArchive : Labels.Archive"
          [disabled]="!sheet?.id"
          (onClick)="archive()"
        />

        <stch-trash-action [disabled]="!sheet?.id" [deleted]="form.controls.deleted.value" (onClick)="delete()" />

        <stch-quick-action
          appearance="sapphire-toggle"
          size="m"
          icon="sapphire-share"
          matTooltip="Share"
          [disabled]="!sheet?.id"
          [stchShareContextMenu]="sheet"
          [stchShareContextMenuDisabled]="!sheet?.id"
        />
      }

      <app-button
        appearance="sapphire-secondary-ghost"
        size="xs"
        icon="sapphire-ellipsis"
        [matTooltip]="S['More options']"
        [ariaLabel]="S['More options']"
        [disabled]="!sheet?.id"
        [appSheetContextMenu]="sheet"
        [appSheetContextMenuDisabled]="!sheet?.id"
        appSheetContextMenuMode="popover"
        [printEnabled]="true"
        (appSheetContextMenuOpenSheet)="expand()"
      />
    </div>

    @if (showCloseButton) {
      <app-button appearance="sapphire-secondary-ghost" size="xs" icon="sapphire-close" [ariaLabel]="S['Close']" (click)="handleClose()" />
    }
  </div>
</div>

<!-- Badge shares the timestamps line, right-aligned, so the header shows where the item lives.
     Only `pr-8` here: app-stitch-timestamps brings its own `px-8`, and this matches the right padding
     the title row above uses, so the badge lines up under the header icons. It hangs 2px under them
     (`self-start -mt-[2px]`), as the prototype's badge column does, rather than centring on the dates. -->
<div class="flex items-center justify-between gap-2 pr-8">
  <!-- AM-38, §7.6, §11.5.2: "Last updated: <time>", with " by Stitchy" when Stitchy made the current revision. -->
  <app-stitch-timestamps
    class="block"
    [createdAt]="sheet?.createdAt"
    [updatedAt]="sheet?.updatedAt"
    [updatedLabel]="S['Last updated']"
    [updatedBy]="sheet?.lastActor === 'stitchy' ? 'Stitchy' : null"
  />
  @if (headerFolder()) {
    <div class="flex min-w-0 self-start -mt-[2px]">
      <ng-container [ngTemplateOutlet]="folderBadge" />
    </div>
  } @else if (locationBadge(); as badge) {
    <app-search-location-badge class="min-w-0 self-start -mt-[2px]" [type]="badge.type" [name]="badge.name ?? ''" />
  }
</div>
}

<div class="tabs skin-detail-tabs">
  <app-tabs appearance="sapphire" [values]="tabsWithCounts()" [value]="selectedTab" (valueChange)="selectTab($event)" size="m" />

  <div class="filler"></div>

  <!-- SBUX-1043: "Open in main app", at the right end of the tab row.
       The handoff names the action in "Intended experience" ("Open panel workbook in main app") while
       its action matrix leaves Open out of both editor ellipses — not a contradiction, because the
       prototype puts it HERE and not in a menu: `fourth-panel-sheets.tsx` renders it as the last child
       of `p4-tabs`, labelled "Open panel workbook in main app" and titled "Open in main app", and the
       design asset `01-edit-in-context.svg` carries an "Icon / open" at x=407 of a 440px panel, level
       with the tabs. Only in the panel's editor: in the main app's you are already there. -->
  @if (dp && inEditor()) {
    <app-button2
      appearance="secondary-ghost"
      size="s"
      data-testid="sheet-form-open-in-main"
      [ariaLabel]="S['Open in main app']"
      [matTooltip]="S['Open in main app']"
      (click)="openInMain.emit()"
    >
      <app-icon name="expand" />
    </app-button2>
  }

  <!-- [MUST-001] 'Assign' button hidden per task; uncomment to re-enable

  <app-button appearance="sapphire-secondary" size="xs" label="Assign" />

  -->
  @if (!inEditor()) {
    <stch-knowledge-button [active]="displayKnowledge" (click)="showKnowledge()" />
  }
</div>

<!-- In the editor, the Sheets tab shows the grid's own Trash banner (S-12, S-13), which reloads the grid on Restore. -->
<!-- Gap row 27: the prototype's lifecycle banner, above the tabs' content, on the app's own lifecycle actions.
     Trash: S-12 and S-13, as the editor words it. Archive: an archived workbook stays editable (§12), so the
     banner says where it is and offers Restore from archive, without the prototype's "Restore it to edit." -->
@if (sheet?.id && form?.controls?.deleted?.value && !(inEditor() && selectedTab === 'sheet')) {
  <div class="lifecycle-banner" role="status" data-testid="sheet-lifecycle-banner">
    <span>{{ S['This workbook is in Trash. Restore it to edit.'] }}</span>
    <button type="button" class="lifecycle-banner-action" [attr.aria-label]="S.Restore" (click)="restoreFromTrash()">{{ S.Restore }}</button>
  </div>
} @else if (sheet?.id && form?.controls?.archived?.value) {
  <div class="lifecycle-banner" role="status" data-testid="sheet-lifecycle-banner">
    <span>{{ S['This workbook is in Archive.'] }}</span>
    <button type="button" class="lifecycle-banner-action" [attr.aria-label]="S['Restore from archive']" (click)="archive()">{{ S['Restore from archive'] }}</button>
  </div>
}

@if (form) {
  <div
    class="form"
    [class.form--editor-grid]="inEditor() && selectedTab === 'sheet'"
    appDroppable
    appDroppableHoverClass="drag-over"
    [appDroppableDisabled]="!stitchItem?.id"
    [appDroppablePredicate]="dndPredicate"
    [formGroup]="form"
    (appDroppableDrop)="dndDrop($event)"
  >
    <div class="drop-area-border"></div>

    @if (selectedTab === 'stitch' && hasKnowledgeHubAccess()) {
      <app-entity-hub-search [contactId]="sheet?.id" />
    }

    <div class="form-heading">
      <div class="form-heading-nav">
        <div class="form-heading-nav-separator"></div>

      </div>

      @if (!knowledgeHidden()) {
        <div class="form-heading-nav-underline"></div>
      }

      <!-- One Knowledge box, rows in the skin's order: Knots · Tags · Connections. On the Stitch tab the rows are
           the knowledge hub's facets (#2481), read-only, and hidden without hub access. -->
      <div
        class="form-knowledge"
        [class.hidden]="knowledgeHidden() || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
      >
        <app-knots
          class="form-knots"
          [class.hidden]="knowledgeHidden() || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
          [stitchItems]="sheet | toArray"
          [control]="form.controls['knots']"
          [stateKey]="StateKey.knotsList"
          [withControls]="selectedTab !== 'stitch'"
          [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
          [hubEntityUid]="sheet?.id"
        />

        <app-tags
          class="form-tags"
          [class.hidden]="knowledgeHidden() || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
          [stitchItems]="[sheet]"
          [control]="form.controls['tags']"
          [stateKey]="StateKey.tagsList"
          [withControls]="selectedTab !== 'stitch'"
          [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
          [hubEntityUid]="sheet?.id"
        />

        <app-connections
          class="form-knots"
          [class.hidden]="knowledgeHidden() || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
          [stitchItems]="[sheet]"
          [control]="form.controls['connections']"
          [stateKey]="StateKey.connectionsList"
          [withControls]="selectedTab !== 'stitch'"
          [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
          [hubEntityUid]="sheet?.id"
        />
      </div>
    </div>

    <!-- In the editor the Sheets tab is the grid itself, below. -->
    @if (
      !(inEditor() && selectedTab === 'sheet') &&
      (selectedTab !== 'stitch' || (permissionsLoaded() && !hasKnowledgeHubAccess()))
    ) {
      <app-scroll class="form-scroll" (top)="displayScrollShadow = !$event">
        @if (displayScrollShadow) {
          <div class="form-scroll-shadow"></div>
        }

        @switch (selectedTab) {
          @case ('sheet') {
            <!-- The detail view shows a PREVIEW, never a live grid: mounting the
                 engine per selection would pull the workbook chunk on every row
                 and run a second render loop behind the list (D-PREVIEW). The
                 button below is the only way to the real editor. -->
            <div class="form-workbook">
              <!-- §11.5.2's action row: the counts, then the primary "Edit workbook" (pencil icon) on the right; a
                   template's is the Design Lab's "+ Use template" (Wook, 2026-10-06). -->
              <div class="form-workbook-bar">
                <span class="form-workbook-counts">
                  @for (part of countLabels(); track $index) {
                    @if (!$first) {
                      <span class="form-workbook-separator"></span>
                    }
                    <span>{{ part }}</span>
                  }
                </span>
                <app-button2
                  class="form-workbook-open"
                  appearance="primary"
                  size="sm"
                  [disabled]="!sheet?.id && !openInWindow()"
                  (click)="sheet?.template ? useTemplate() : openWorkbook()"
                  ><svg class="form-workbook-pencil" viewBox="0 0 24 24" aria-hidden="true">
                    <path [attr.d]="sheet?.template ? 'M12 5v14M5 12h14' : 'M4 20h4L19 9l-4-4L4 16z M13.5 6.5l4 4'" /></svg
                  >{{ sheet?.template ? S['Use template'] : S['Edit workbook'] }}</app-button2
                >
              </div>

              <app-workbook-preview class="form-workbook-preview" [snapshot]="previewSnapshot()" (openSheet)="expand($event)" />

              <!-- Gap row 25: the prototype's "Description" under the preview: the same field as on Details, saved
                   when it loses focus, as the prototype's textarea is. -->
              <stch-item-description-field
                class="form-workbook-description"
                data-testid="sheet-workbook-description"
                [label]="S.Description"
                [control]="form.controls.description"
                [item]="sheet"
                [tagsControl]="form.controls['tags']"
                [knotsControl]="form.controls['knots']"
                (blurred)="saveDescriptionOnBlur()"
              />

              <!-- Gap row 26: "Connected context <n>", the stitched items, each opening in the Dynamic Panel, as the
                   prototype's open in its companion pane. -->
              <div class="form-workbook-linked" data-testid="sheet-workbook-connected">
                <h3 class="form-workbook-heading">
                  {{ S['Connected context'] }}<span class="form-workbook-count">{{ connectedCount() }}</span>
                </h3>
                @if (connectedCount() > 0) {
                  <app-linked-info-list-short
                    [stitch]="sheet"
                    [state]="connectedListState"
                    [withActions]="false"
                    [withUnlinkAll]="false"
                    [withPlaceholderImage]="false"
                    (clickStitchItem)="handleClickItem($event)"
                    (dblClickStitchItem)="handleDblClickItem($event)"
                  />
                }
                <!-- Add Stitch removed (QA, 2026-10-08): buggy, and the other apps have none. Stitching stays by drag and drop. -->
              </div>
            </div>
          }

          @case ('details') {
            <!-- Gap row 29: §11.5.3's fields (Title, Created, Last updated, Sheets, Cells with content, Formula cells,
                 Storage, Engine version), with the Description and Folder the tab already had, and the prototype's
                 Owner (read-only), Revision and Status, in the prototype's order. -->
            <div class="details" [class.contracted]="contracted">
              <div class="details-row">
                <div class="details-row-label">{{ S.Title }}</div>
                <app-input
                  class="details-row-input"
                  [style.width]="contracted ? '100%' : '340px'"
                  appearance="sapphire-outline"
                  [inputFormControl]="form.controls.title"
                  [placeholder]="S.Title"
                  [errorPlaceholder]="form.controls.title.hasError('required') ? 'Add title' : undefined"
                  [validationMessages]="titleMessages"
                  [withErrors]="submitted"
                  size="s"
                  data-testid="sheet-details-title"
                />
              </div>

              <stch-item-description-field
                class="details-description"
                data-testid="sheet-details-description"
                [label]="S.Description"
                [control]="form.controls.description"
                [item]="sheet"
                [tagsControl]="form.controls['tags']"
                [knotsControl]="form.controls['knots']"
              />

              <div class="details-row">
                <div class="details-row-label">Folder</div>

                <app-autocomplete-input
                  class="details-row-input"
                  [style.width]="contracted ? '100%' : '252px'"
                  appearance="sapphire-outline"
                  placeholder="Folder"
                  [suggestions]="suggestions"
                  [inputFormControl]="form.controls.sheetFolderId"
                  size="s"
                  [withErrors]="submitted"
                  [withClear]="true"
                />
              </div>

              <!-- The prototype's Owner, beside Folder: read-only, the user who made the workbook (Wook, 2026-10-07). -->
              <div class="details-row">
                <div class="details-row-label">{{ S.Owner }}</div>
                <div
                  class="details-row-input details-row-readonly"
                  [style.width]="contracted ? '100%' : '252px'"
                  [attr.aria-label]="S.Owner"
                  data-testid="sheet-details-owner"
                >
                  {{ owner(space$ | async) }}
                </div>
              </div>

              <!-- The read-only facts: §11.5.3's, with the prototype's Revision and Status after Last updated. -->
              <dl class="details-facts" data-testid="sheet-details-facts">
                <dt>{{ S.Created }}</dt>
                <dd>{{ sheet?.createdAt | dateFormat: 'dateTime' }}</dd>
                <dt>{{ S['Last updated'] }}</dt>
                <dd>
                  {{ sheet?.updatedAt | dateFormat: 'dateTime' }}@if (sheet?.lastActor === 'stitchy') {
                    {{ S['by Stitchy'] }}
                  }
                </dd>
                <dt>{{ S.Revision }}</dt>
                <dd>{{ sheet?.revision }}</dd>
                <dt>{{ S.Status }}</dt>
                <dd>{{ status() }}</dd>
                @if (facts(); as f) {
                  <dt>{{ S.Sheets }}</dt>
                  <dd>{{ f.sheets }}</dd>
                  <dt>{{ S['Cells with content'] }}</dt>
                  <dd>{{ f.cells }}</dd>
                  <dt>{{ S['Formula cells'] }}</dt>
                  <dd>{{ f.formulas }}</dd>
                }
                @if (storage(); as size) {
                  <dt>{{ S.Storage }}</dt>
                  <dd>{{ size }}</dd>
                }
                @if (facts()?.engine; as engine) {
                  <dt>{{ S['Engine version'] }}</dt>
                  <dd>{{ engine }}</dd>
                }
              </dl>
            </div>
          }

          @case ('hub') {
            @if (!permissionsLoaded()) {
              <!-- Render nothing until permission rules arrive to avoid flashing the wrong pane -->
            } @else if (hasKnowledgeHubAccess()) {
              <app-knowledge-hub [item]="sheet" />
            } @else {
              <app-feature-upsell
                title="Knowledge Hub"
                description="AI-powered summaries, entities, themes, and key terms for your items. Upgrade your plan to unlock Knowledge Hub."
                imageSrc="assets/placeholders/placeholder-files.svg"
              />
            }
          }

          @case ('attachments') {
            <app-attachments-list
              class="form-attachments"
              [stitchItem]="stitchItem"
              [inputControl]="form.controls.uploads"
              [contextMenuStateVariant]="attachmentsMenuStateVariant"
              (listStateChange)="attachmentsListState = $event"
              (onClickItem)="handleClickItem($event)"
              (onDblClickItem)="handleDblClickItem($event)"
              (uploading)="onUploadingChange($event)"
            />
          }

          @case ('stitch') {
            <!-- Gap row 30: the prototype's heading, "Stitched items <n>", before the list's own sort and filter menu.
                 The count is the tab badge's: what the list shows. -->
            <div class="form-section-heading" data-testid="sheet-stitch-heading">
              <h2 class="form-section-title">
                {{ S['Stitched items'] }}<span class="form-section-count">{{ stitchTabCount() ?? 0 }}</span>
              </h2>
              <!-- Add Stitch removed (QA, 2026-10-08): buggy, and the other apps have none. Stitching stays by drag and drop. -->
              <app-context-menu-state-indicator
                svgIcon="filters"
                appStitchListContextMenu
                (appStitchListContextMenuChange)="stitchListState = $event"
              />
            </div>

            <app-linked-info-list
              class="form-linked-info"
              [stitch]="sheet"
              [state]="stitchListState"
              (clickStitchItem)="handleClickItem($event)"
              (dblClickStitchItem)="handleDblClickItem($event)"
            />
          }

          @case ('comments') {
            <stch-comments class="form-linked-info" [stitch]="sheet" />
          }

          @case ('history') {
            @if (sheet?.id) {
              <app-sheet-version-history-lazy
                [workbookId]="sheet.id"
                [revision]="sheet.revision"
                [readOnly]="!!form?.controls?.deleted?.value"
                [tenantId]="sheet.accountId"
              />
            }
          }

          @case ('debug') {
            <stch-json-viewer [json]="sheet?.debug" />
          }
        }
      </app-scroll>
    }

    @if (selectedTab === 'stitch' && hasKnowledgeHubAccess()) {
      <app-stitch-hub
        [stitch]="sheet"
        (clickStitchItem)="handleClickItem($event)"
        (dblClickStitchItem)="handleDblClickItem($event)"
      />
    }

    @if (!inEditor()) {
      <stch-save-cancel-panel
        [enabled]="!pristine"
        [uploading]="isUploading()"
        (onCancel)="handleCancel()"
        (onSave)="handleSubmit()"
      />
    }

  </div>
}

@if (inEditor()) {
  <!-- SBUX-1036: the editor, this page's Sheets tab. On another tab it stays mounted and laid out at the size it had,
       hidden (the prototype's `editor-hidden`: visibility, not display, which would give the canvas no size), so the
       engine is never rebuilt and has nothing to re-measure when it shows again. -->
  <div
    #editorSurface
    class="editor-surface"
    [class.sheets-editor-hidden]="selectedTab !== 'sheet'"
    [style.--sheets-editor-top.px]="editorTop"
    [attr.aria-hidden]="selectedTab !== 'sheet' ? 'true' : null"
    data-testid="sheet-editor-surface"
  >
    <ng-content />
  </div>
  @if (form) {
    <!-- Below every tab, the grid's included: what is typed on Details waits here to be saved. -->
    <stch-save-cancel-panel [enabled]="!pristine" [uploading]="isUploading()" (onCancel)="handleCancel()" (onSave)="handleSubmit()" />
  }
}

<!-- stitch-app-shell breadcrumbs footer in the main view, as in every app (not over the grid editor). -->
@if (navigation.enabled && sheet?.id && !inEditor()) {
  <app-detail-breadcrumbs
    [crumbs]="breadcrumbs()"
    [status]="sheet.archived ? 'Archived' : sheet.deleted ? 'Trash' : undefined"
  />
}

<!-- stitch-app-shell FolderBadge: the sheet's folder in its colour. -->
<ng-template #folderBadge>
  @if (headerFolder(); as folder) {
    <app-container-badge
      type="sheet-folder"
      class="min-w-0"
      [title]="folder.title"
      [color]="folder.color"
    />
  }
</ng-template>
